import { EXTREME_BET_CENTS, EXTREME_PAID_SPINS, EXTREME_START_BALANCE_CENTS } from './types';
import type { ExtremeFeatureLeaderboardEntry, ExtremeRunLeaderboardEntry } from './types';

type UnknownRecord = Record<string, unknown>;

function record(value: unknown): UnknownRecord | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? value as UnknownRecord : null;
}

function integer(value: unknown, min = 0, max = Number.MAX_SAFE_INTEGER): number | null {
  return Number.isSafeInteger(value) && Number(value) >= min && Number(value) <= max ? Number(value) : null;
}

function finite(value: unknown, min = 0, max = Number.MAX_VALUE): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max ? value : null;
}

function identifier(value: unknown, fallback?: string): string | null {
  const candidate = typeof value === 'string' ? value : fallback;
  return candidate && /^[A-Za-z0-9_-]{1,128}$/.test(candidate) ? candidate : null;
}

function playerName(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 && value.length <= 20 ? value : null;
}

function optionalUid(value: unknown): string | undefined {
  return typeof value === 'string' && /^[A-Za-z0-9_-]{1,128}$/.test(value) ? value : undefined;
}

function approximatelyEqual(left: number, right: number): boolean {
  return Math.abs(left - right) <= 0.000_001;
}

export function decodeExtremeRunEntry(value: unknown, fallbackId?: string): ExtremeRunLeaderboardEntry | null {
  const source = record(value);
  if (source === null) return null;
  if ((source.schemaVersion !== undefined && source.schemaVersion !== 1)
    || (source.seasonId !== undefined && source.seasonId !== 'season3')
    || (source.mode !== undefined && source.mode !== 'extreme')
    || (source.paidSpins !== undefined && source.paidSpins !== EXTREME_PAID_SPINS)
    || (source.betCents !== undefined && source.betCents !== EXTREME_BET_CENTS)
    || (source.startBalanceCents !== undefined && source.startBalanceCents !== EXTREME_START_BALANCE_CENTS)) return null;
  const id = identifier(source.clientEntryId ?? source.id, fallbackId);
  const name = playerName(source.name);
  const finalBalanceCents = integer(source.finalBalanceCents, 0, 1_000_000_000);
  const featureCount = integer(source.featureCount, 0, EXTREME_PAID_SPINS);
  const createdAt = integer(source.createdAt, 1);
  const scoreX = finite(source.scoreX, 0, 5_000_000);
  const scoreMicros = source.scoreMicros === undefined ? undefined : integer(source.scoreMicros, 0, 5_000_000_000_000);
  if (!id || !name || finalBalanceCents === null || featureCount === null || createdAt === null || scoreX === null
    || !approximatelyEqual(scoreX, finalBalanceCents / EXTREME_BET_CENTS)
    || (scoreMicros !== undefined && (scoreMicros === null || Math.abs(scoreMicros - scoreX * 1_000_000) > 1))) return null;
  const ownerUid = optionalUid(source.uid ?? source.ownerUid);
  return Object.freeze({
    id, name, scoreX, finalBalanceCents, featureCount, createdAt, replayAvailable: false,
    ...(ownerUid ? { ownerUid } : {}),
  });
}

export function decodeExtremeFeatureEntry(value: unknown, fallbackEntryId?: string): ExtremeFeatureLeaderboardEntry | null {
  const source = record(value);
  if (source === null) return null;
  if ((source.schemaVersion !== undefined && source.schemaVersion !== 1)
    || (source.seasonId !== undefined && source.seasonId !== 'season3')
    || (source.mode !== undefined && source.mode !== 'extreme')
    || (source.betCents !== undefined && source.betCents !== EXTREME_BET_CENTS)
    || (source.replayAvailable !== undefined && source.replayAvailable !== true)) return null;
  const runId = identifier(source.runId ?? source.clientEntryId);
  const id = identifier(source.replayId ?? source.id, fallbackEntryId);
  const name = playerName(source.name);
  const winCents = integer(source.winCents, 0, 1_000_000_000);
  const totalSpins = integer(source.totalSpins, 1, 10_000);
  const createdAt = integer(source.createdAt, 1);
  const xValue = finite(source.xValue, 0, 5_000_000);
  const xMicros = source.xMicros === undefined ? undefined : integer(source.xMicros, 0, 5_000_000_000_000);
  if (!runId || !id || !name || winCents === null || totalSpins === null || createdAt === null || xValue === null
    || !approximatelyEqual(xValue, winCents / EXTREME_BET_CENTS)
    || (xMicros !== undefined && (xMicros === null || Math.abs(xMicros - xValue * 1_000_000) > 1))) return null;
  const ownerUid = optionalUid(source.uid ?? source.ownerUid);
  return Object.freeze({
    id, runId, name, xValue, winCents, totalSpins, createdAt, replayAvailable: true,
    ...(ownerUid ? { ownerUid } : {}),
  });
}

export function buildRemoteExtremeRun(
  entry: ExtremeRunLeaderboardEntry,
  uid: string,
): Readonly<Record<string, unknown>> {
  return Object.freeze({
    schemaVersion: 1,
    seasonId: 'season3',
    mode: 'extreme',
    uid,
    clientEntryId: entry.id,
    name: entry.name,
    paidSpins: EXTREME_PAID_SPINS,
    betCents: EXTREME_BET_CENTS,
    startBalanceCents: EXTREME_START_BALANCE_CENTS,
    finalBalanceCents: entry.finalBalanceCents,
    featureCount: entry.featureCount,
    scoreMicros: Math.round(entry.scoreX * 1_000_000),
    scoreX: Number(entry.scoreX.toFixed(6)),
    createdAt: entry.createdAt,
  });
}

export function buildRemoteExtremeFeature(
  entry: ExtremeFeatureLeaderboardEntry,
  uid: string,
): Readonly<Record<string, unknown>> {
  return Object.freeze({
    schemaVersion: 1,
    seasonId: 'season3',
    mode: 'extreme',
    uid,
    clientEntryId: entry.id,
    runId: entry.runId,
    replayId: entry.id,
    name: entry.name,
    betCents: EXTREME_BET_CENTS,
    winCents: entry.winCents,
    xMicros: Math.round(entry.xValue * 1_000_000),
    xValue: Number(entry.xValue.toFixed(6)),
    totalSpins: entry.totalSpins,
    createdAt: entry.createdAt,
    replayAvailable: true,
  });
}
